<template>
	<div class="w-full pt-8">
		<div class="bg-white border-gray-200 flex w-full flex-col items-start rounded-lg border lg:flex-row">
			<div class="flex flex-shrink-0 items-center justify-center self-stretch pe-8 ps-8 lg:self-start">
				<div :class="['-mt-8 mb-2 flex h-24 w-24 items-center justify-center lg:mb-8', status.txtClass]">
					<Icon name="mdi:alert" class="h-24 w-24" />
				</div>
			</div>
			<div class="bg-re flex w-full flex-grow flex-col pb-6 ps-6 pt-6 lg:pb-4 lg:ps-0 lg:pt-4">
				<div class="flex items-center">
					<span class="text-neutral-800 relative font-bold duration-300">
						<span
							:class="[
								'absolute bottom-0 start-0 -ms-3 mb-1 h-2 w-10 rounded-full opacity-20',
								status.bgClass
							]"
						/>
						<span class="absolute left-0 top-0 line-clamp-1 block">
							{{ (data && data.title) || "~" }}
						</span>
						<span class="z-1 line-clamp-1 block select-none opacity-0">
							{{ (data && data.title) || "~" }}
						</span>
					</span>
					<div class="bg-gray-200 me-8 ms-8 h-6 w-px flex-shrink-0" />
					<span
						:class="[
							'flex items-center rounded-full bg-opacity-10 py-0.5 pe-1 ps-2 text-xs',
							hideStatusEdit ? '' : 'cursor-pointer',
							status.bgClass,
							status.txtClass
						]"
						@click="!hideStatusEdit && emit('status')"
					>
						<span>
							{{ status.name }}
						</span>
						<Icon v-if="!hideStatusEdit" name="mdi:pencil" class="ms-1 h-3 w-3" />
					</span>
					<div class="flex-grow" />
					<div class="me-6 flex items-center gap-6">
						<span
							v-if="editable"
							class="text-neutral-700 bg-red-500 flex cursor-pointer items-center rounded-full bg-opacity-0 duration-300 hover:text-red-500 hover:bg-opacity-10"
							@click="emit('delete')"
						>
							<Icon name="mdi:delete" class="h-8 w-8 p-1" />
						</span>
						<span
							v-if="!hideMore"
							class="text-neutral-700 flex cursor-pointer items-center rounded-full bg-secondary bg-opacity-0 duration-300 hover:bg-opacity-10 hover:text-secondary rtl:rotate-180"
							@click="emit('more')"
						>
							<Icon name="mdi:arrow-left" class="h-8 w-8 rotate-180 transform p-1" />
						</span>
					</div>
				</div>
				<div class="mt-4 grid grid-cols-1 gap-3 self-stretch sm:grid-cols-2">
					<div v-if="data?.company_id" class="flex items-center text-sm">
						<Icon name="mdi:office-building" class="text-neutral-700 me-1 h-6 w-6" />
						<span class="text-neutral-700">
							{{ $t("company") }}
						</span>
						<span class="text-neutral-700 mx-2">:</span>
						<span class="text-neutral-700">
							{{ data?.company_title || "~" }}
						</span>
					</div>
					<div class="flex items-center text-sm">
						<Icon name="mdi:account" class="text-neutral-700 me-1 h-6 w-6" />
						<span class="text-neutral-700">
							{{ $t("reporter") }}
						</span>
						<span class="text-neutral-700 mx-2">:</span>
						<span class="text-neutral-700">
							{{ (data?.user_firstname || "~") + " " + (data?.user_lastname || "~") }}
						</span>
					</div>
					<div class="flex items-center text-sm">
						<Icon name="mdi:alert" class="text-neutral-700 me-1 h-6 w-6" />
						<span class="text-neutral-700">
							{{ $t("priority") }}
						</span>
						<span class="text-neutral-700 mx-2">:</span>
						<span class="font-medium" :class="[priority.txtClass]">
							{{ priority.name }}
						</span>
					</div>
					<div class="flex items-center text-sm">
						<Icon name="mdi:clock-time-nine" class="text-neutral-700 me-1 h-6 w-6" />
						<span class="text-neutral-700">
							{{ $t("creation_date") }}
						</span>
						<span class="text-neutral-700 mx-2">:</span>
						<span class="text-neutral-700">
							{{ getDateString(data && data.created) || "~" }}
						</span>
					</div>
					<div class="flex items-center text-sm">
						<Icon name="mdi:clock-time-nine" class="text-neutral-700 me-1 h-6 w-6" />
						<span class="text-neutral-700">
							{{ $t("last_update") }}
						</span>
						<span class="text-neutral-700 mx-2">:</span>
						<span class="text-neutral-700">
							{{ getDateString(data && data.updated) || "~" }}
						</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { DevTicket } from "~/models/dev-ticket"

const moment = useMoment()
const devTicketStore = useDevTicketStore()

const props = withDefaults(
	defineProps<{
		data: DevTicket | null
		hideMore?: boolean
		hideStatusEdit?: boolean
		editable?: boolean
	}>(),
	{
		editable: true
	}
)

const emit = defineEmits<{
	delete: []
	edit: []
	status: []
	more: []
}>()

const statuses = computed(() => devTicketStore.statuses)
const priorities = computed(() => devTicketStore.priorities)
const status = computed(() => statuses.value.find((v) => v.value === props.data?.status) || statuses.value[0])
const priority = computed(() => priorities.value.find((v) => v.value === props.data?.priority) || priorities.value[0])

const getDateString = (d: any) => {
	if (!d) return "~"
	return moment.utc(d, "YYYY/MM/DD").local().format("YYYY/MM/DD")
}
</script>
